// What an agent asked a database, and what it changed.
//
//   docker exec ae2e-orch-pg psql -U postgres -d ae2e_orchestrator -tAc "SELECT count(*) FROM run_report;"
//   psql -c "SELECT id, state FROM run WHERE id='…'"
//   curl -sG "$CH/" --data-urlencode "query=SELECT count() FROM events.events WHERE …"
//
// The target is the TABLE, not the query. Two agents asking different questions of
// `run_report` are both telling you about `run_report`, and a reader who knows the
// schema knows what that is — which is the identifiability the tab axis wants.
//
// The reading is the same shape as the live lens, one layer down: reading a table
// is not the same act as writing one, and a row count is PLATFORM schema — a
// count is a count on every database in the world — so it accumulates into
// last-known state without a word of domain knowledge. What a `SELECT id, state`
// returned does not, and travels as evidence.

import { flagOf, shellStatements } from "../../src/mine/statements.js";

const CLIENTS = /^(?:psql|mysql|clickhouse-client|sqlite3|cockroach|mongosh)$/i;
const DML = /^(?:insert|update|delete|merge|upsert|truncate)$/i;
const DDL = /^(?:create|alter|drop|rename|grant|revoke|comment)$/i;

/** The SQL a client invocation carries, from wherever it was passed. */
export function sqlOf(st) {
  const inline = flagOf(st.args, "-c", "--command", "-tAc", "-Ac", "-tc", "-e", "--execute", "--query");
  if (inline) return inline;
  // `-tAc` and friends are bundled short flags; the SQL is the next token.
  const bundled = st.args.findIndex((a) => /^-[a-zA-Z]*[ceq][a-zA-Z]*$/.test(a) && a.length <= 6);
  if (bundled !== -1 && st.args[bundled + 1] && !st.args[bundled + 1].startsWith("-")) return st.args[bundled + 1];
  const urlencoded = /(?:^|[\s"'])query=([\s\S]+?)(?:["']\s|["']$|$)/.exec(st.text);
  if (urlencoded) return urlencoded[1];
  return null;
}

/** The statement kind, from the leading keyword. */
export function sqlVerbOf(sql) {
  const m = /^\s*(?:\/\*[\s\S]*?\*\/\s*)?(?:with\b[\s\S]*?\)\s*)?(\w+)/i.exec(String(sql || ""));
  const v = (m?.[1] || "").toLowerCase();
  if (!v) return null;
  if (v === "select" || v === "show" || v === "explain" || v === "describe" || v === "with") return "read";
  if (DML.test(v)) return "write";
  if (DDL.test(v)) return "schema";
  return "other";
}

/**
 * Tables the statement names. Only real identifiers: a bare `1` in `SELECT 1` names
 * nothing, and a lens that invented a table for it would report a health check as a
 * query against a table called 1.
 */
export function tablesOf(sql) {
  const s = String(sql || "");
  const found = [];
  for (const re of [/\bfrom\s+([a-z_][\w.$]*)/gi, /\bjoin\s+([a-z_][\w.$]*)/gi,
    /\binto\s+([a-z_][\w.$]*)/gi, /\bupdate\s+([a-z_][\w.$]*)/gi,
    /\b(?:table|index\s+on)\s+(?:if\s+not\s+exists\s+)?([a-z_][\w.$]*)/gi]) {
    for (const m of s.matchAll(re)) {
      const t = m[1].toLowerCase();
      if (/^(?:select|where|values|set|dual|only)$/.test(t)) continue;
      if (!found.includes(t)) found.push(t);
    }
  }
  return found;
}

/** A row count, when the query asked for one and the client printed a bare number. */
export function rowCountOf(sql, output) {
  if (!output || !/\bcount\s*\(/i.test(String(sql || ""))) return null;
  // `-tA` prints one unadorned value; anything else is a formatted table this does
  // not try to read, because guessing at a layout is how a count becomes wrong.
  const m = /^\s*(\d{1,12})\s*$/m.exec(output);
  return m ? Number(m[1]) : null;
}

const DENIED_RE = /permission for this action was denied|\bpermission denied\b|\bnot authorized\b|\baccess denied\b/i;
const DB_ERROR_RE = /\bERROR:\s|\bFATAL:\s|does not exist|syntax error at or near|Code:\s*\d+\.\s*DB::Exception/i;

/**
 * A client nested inside another program. The dominant form in real traces is
 * `docker exec pg psql -tAc "…"` or `kubectl exec pod -- psql …`, where the outer
 * program is the carrier and the database client is an argument. Reading only the
 * leading program missed every one of them.
 */
function nestedClient(st) {
  const at = st.argv.findIndex((t) => CLIENTS.test(String(t).split(/[\\/]/).pop().replace(/\.exe$/i, "")));
  if (at === -1) return null;
  return { program: st.argv[at].split(/[\\/]/).pop(), args: st.argv.slice(at + 1) };
}

export function extractSqlOps(turns) {
  const ops = [];
  for (const raw of shellStatements(turns)) {
    const nested = CLIENTS.test(raw.program) ? null : nestedClient(raw);
    // Re-point the statement at the client, keeping the carrier's text and result.
    const st = nested ? { ...raw, program: nested.program, args: nested.args } : raw;
    const isClient = CLIENTS.test(st.program);
    const sql = sqlOf(st);
    // A curl carrying `query=` is a database client too; a curl without one is not.
    if (!isClient && !(sql && /\b(?:select|insert|update|delete|create|alter|drop|show)\b/i.test(sql))) continue;
    if (!sql) continue;
    const verb = sqlVerbOf(sql);
    if (!verb || verb === "other") continue;

    const database = flagOf(st.args, "-d", "--dbname", "--database")
      || (/\bdatabase=([\w.-]+)/i.exec(st.text)?.[1] || null);
    const host = flagOf(st.args, "-h", "--host")
      || (/https?:\/\/([^/\s"']+)/.exec(st.text)?.[1] || null);
    const tables = tablesOf(sql);
    const out = st.output;

    ops.push({
      key: st.key,
      verb,
      sql: sql.replace(/\s+/g, " ").trim().slice(0, 600),
      tables,
      database,
      host,
      // A count is a count on every database: platform schema, safe to accumulate.
      rows: rowCountOf(sql, out),
      outcome: !out && st.outputStatus !== "observed" ? "unrecorded"
        : out && DENIED_RE.test(out) ? "denied"
        : out && DB_ERROR_RE.test(out) ? "errored"
        : out ? "answered" : "unrecorded",
      // Not attributable when the call ran several statements: one result blob
      // cannot be split between them.
      shared: st.siblings > 1,
      carrier: nested ? raw.program : null,
      output: out,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind: verb, cmd: sql.replace(/\s+/g, " ").trim().slice(0, 280) }],
      checks: [{
        kind: verb === "read" ? "queried" : verb,
        tool: [database, tables[0]].filter(Boolean).join(".") || "unnamed",
        scope: [
          // The answer, when the query asked for a count and the client printed one.
          rowCountOf(sql, out) != null ? `${rowCountOf(sql, out)} rows` : null,
          host,
          tables.length > 1 ? `${tables.length} tables` : null,
        ].filter(Boolean),
        outcome: !out ? "unrecorded" : DENIED_RE.test(out) ? "denied" : DB_ERROR_RE.test(out) ? "errored" : "answered",
      }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — what the database returned never entered this trace";
    case "shell-error": return "the captured text was the shell's error, not the database's response";
    case "none": return "ran and captured no output";
    default: break;
  }
  if (op.outcome === "denied") return "the database refused the statement — the agent has no answer from it";
  if (op.outcome === "errored") return "the statement failed against the schema; the agent learned the query was wrong, not the answer";
  if (op.shared) return "this call ran several statements and returned one result — which part belongs to this statement is not recoverable";
  return null;
}
